import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePublishedPolicy from "../hooks/usePublishedPolicy";
import PolicyPage, { PolicyLoading } from "../components/PolicyText";
import {
  COMPANY_NAME,
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_HREF,
  EFFECTIVE_DATE,
  GRIEVANCE_OFFICER_NAME,
  JURISDICTION_CITY,
  LAST_UPDATED,
  REGISTERED_ADDRESS,
} from "../data/privacyPolicy";

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ocean-800/70 dark:text-foam-100/70">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" />
          {item}
        </li>
      ))}
    </ul>
  );
}

interface SectionProps {
  id: string;
  number: number;
  title: string;
  delay?: number;
  children: ReactNode;
}

function Section({ id, number, title, delay = 0, children }: SectionProps) {
  return (
    <div id={id} className="scroll-mt-24">
      <ScrollReveal delay={delay}>
        <h2 className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">
          {number}. {title}
        </h2>
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-ocean-800/70 dark:text-foam-100/70">
          {children}
        </div>
      </ScrollReveal>
    </div>
  );
}

/** The built-in text: shown until staff publish a version of the Privacy Policy in the admin panel (Policy & legal). */
function BuiltInPrivacy() {
  useDocumentTitle("Privacy Policy");
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={10} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Legal
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Effective Date: {EFFECTIVE_DATE} · Last Updated: {LAST_UPDATED}
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      <section className="mx-auto max-w-3xl space-y-14 px-6 py-24">
        <Section id="introduction" number={1} title="Introduction">
          <p>
            {COMPANY_NAME} (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) is committed to protecting your
            privacy and handling your personal information responsibly. This Privacy Policy explains how we
            collect, use, disclose, store, and safeguard information when you use the {COMPANY_NAME} mobile
            application, website, and related services (collectively, the &quot;Service&quot;).
          </p>
          <p>
            This Policy is framed in accordance with the Digital Personal Data Protection Act, 2023 and the
            Digital Personal Data Protection Rules, 2025 (together, the &quot;DPDP Act&quot;), the Information
            Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures
            and Sensitive Personal Data or Information) Rules, 2011, and the Consumer Protection (E-Commerce)
            Rules, 2020.
          </p>
          <p>
            This Policy addresses the processing of your personal data only. Separate obligations apply to{" "}
            {COMPANY_NAME} under the Food Safety and Standards Act, 2006 and FSSAI regulations, and the Legal
            Metrology Act, 2009, in relation to food safety, licensing, and product labelling — these are
            outside the scope of this document.
          </p>
          <p>
            By accessing or using the Service, you agree to the collection and use of information as described
            in this Policy. If you do not agree, please do not use the Service.
          </p>
        </Section>

        <Section id="applicability" number={2} title="Who This Policy Applies To" delay={0.02}>
          <p>
            The Service is intended for use by individuals located in India who are 18 years of age or older.
            If you are under 18, please see Section 13 (&quot;Children&apos;s Privacy&quot;) before using the
            Service.
          </p>
        </Section>

        <Section id="information-we-collect" number={3} title="Information We Collect" delay={0.02}>
          <h3 className="font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            3.1 Personal Information
          </h3>
          <p>When you create an account, place an order, or contact us, we may collect:</p>
          <Bullets
            items={[
              "Full Name",
              "Mobile Number",
              "Email Address",
              "Delivery Address",
              "Billing Address",
              "Profile Information",
              "Customer Support Communications (including call recordings, chat transcripts, and photographs shared to resolve a quality or delivery issue)",
            ]}
          />

          <h3 className="mt-6 font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            3.2 Order Information
          </h3>
          <p>We collect information related to your purchases, including:</p>
          <Bullets
            items={["Ordered Products", "Order History", "Payment Details (see Section 6)", "Delivery Preferences", "Transaction Records"]}
          />

          <h3 className="mt-6 font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            3.3 Location Information
          </h3>
          <p>To provide delivery services, we may collect:</p>
          <Bullets items={["Delivery Address", "Approximate Location Information", "Device Location (only with your permission)"]} />
          <p>Location information is used solely to improve delivery accuracy, route planning, and customer experience.</p>

          <h3 className="mt-6 font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            3.4 Device and Usage Information
          </h3>
          <p>We may automatically collect:</p>
          <Bullets
            items={["Device Type", "Operating System", "App Version", "IP Address", "Device Identifiers", "App Usage Data", "Crash Reports and Diagnostics"]}
          />

          <h3 className="mt-6 font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            3.5 Cookies and Similar Technologies
          </h3>
          <p>
            Our website and app may use cookies, SDKs, and similar tracking technologies to keep you signed in,
            remember your preferences, understand how the Service is used, and improve performance. Where
            required, we will present a cookie/consent banner allowing you to accept or reject non-essential
            cookies. You can also control cookies through your browser or device settings; disabling some
            cookies may affect parts of the Service. See our{" "}
            <Link to="/compliance#cookie-policy" className="font-semibold text-teal-600 hover:text-teal-700 dark:text-teal-300">
              Cookie Policy
            </Link>{" "}
            for details.
          </p>
        </Section>

        <Section id="purposes" number={4} title="Purposes for Which We Use Your Information" delay={0.02}>
          <p>We use collected information to:</p>
          <Bullets
            items={[
              "Process and fulfill orders",
              "Schedule and manage deliveries",
              "Verify customer accounts",
              "Communicate order updates, substitutions, cancellations, and refunds",
              "Respond to customer inquiries and resolve complaints",
              "Improve our products and services",
              "Personalize your experience",
              "Detect and prevent fraud and unauthorized activity",
              "Send service-related and, where you have consented, promotional communications",
              "Comply with legal obligations",
            ]}
          />
          <p>
            We only process personal data for the specific purposes disclosed to you at the time of collection,
            and for purposes reasonably connected to them, in line with the purpose-limitation principle under
            the DPDP Act.
          </p>
        </Section>

        <Section id="consent" number={5} title="Consent and Your Choices" delay={0.02}>
          <p>
            Where our processing of your personal data relies on your consent, you may give, refuse, or
            withdraw that consent at any time. Withdrawing consent is as simple as giving it — you can do this
            from the app&apos;s account/privacy settings or by writing to us at the contact details in Section
            15. Withdrawal will not affect the lawfulness of any processing carried out before withdrawal, and
            may result in our being unable to provide certain features of the Service (for example, we cannot
            deliver an order without a delivery address).
          </p>
        </Section>

        <Section id="payment-information" number={6} title="Payment Information" delay={0.02}>
          <p>
            Payments made through {COMPANY_NAME} are processed by third-party, RBI-authorized payment service
            providers and payment aggregators. We do not store complete debit/credit card numbers, UPI PINs,
            net banking credentials, or other sensitive payment authentication data on our servers. Payment
            information is handled by our payment partners in accordance with applicable RBI guidelines and
            industry security standards (such as PCI-DSS).
          </p>
        </Section>

        <Section id="availability" number={7} title="Product Availability and Order Fulfillment" delay={0.02}>
          <p>
            {COMPANY_NAME} specializes in fresh seafood sourced daily from local fishing harbors. Because
            seafood availability depends on daily market conditions, weather, fishing activity, and quality
            inspections, product availability cannot always be guaranteed. We may use your order and contact
            information to communicate substitutions, modifications, cancellations, refunds, or delivery
            updates where necessary.
          </p>
        </Section>

        <Section id="data-retention" number={8} title="Data Retention" delay={0.02}>
          <p>
            We retain personal data only for as long as necessary to fulfill the purposes described in this
            Policy, including to provide the Service, comply with our legal, accounting, and tax obligations,
            resolve disputes, and enforce our agreements. Order and transaction records are typically retained
            for the period required under applicable tax and consumer-protection laws. Where you delete your
            account or withdraw consent, we will delete or anonymize your personal data unless retention is
            required by law.
          </p>
        </Section>

        <Section id="sharing" number={9} title="Sharing of Information" delay={0.02}>
          <p>We do not sell, rent, or trade your personal information.</p>

          <h3 className="mt-6 font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            9.1 Delivery Partners
          </h3>
          <p>To complete deliveries, we may share:</p>
          <Bullets items={["Name", "Phone Number", "Delivery Address", "Order Details necessary for delivery"]} />

          <h3 className="mt-6 font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            9.2 Service Providers
          </h3>
          <p>We may engage trusted third parties, acting on our instructions and bound by confidentiality and data protection obligations, to assist with:</p>
          <Bullets items={["Payment Processing", "Cloud Hosting", "Customer Support", "Analytics", "Technical Maintenance"]} />

          <h3 className="mt-6 font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            9.3 Legal Requirements
          </h3>
          <p>We may disclose information if required:</p>
          <Bullets items={["By law", "By court order", "By government or regulatory authorities", "To protect our rights, customers, employees, or business operations"]} />

          <h3 className="mt-6 font-display text-base font-semibold text-ocean-950 dark:text-foam-50">
            9.4 Business Transfers
          </h3>
          <p>
            If {COMPANY_NAME} is involved in a merger, acquisition, restructuring, or sale of assets, personal
            data may be transferred as part of that transaction. We will notify you before your personal data
            becomes subject to a different privacy policy.
          </p>
        </Section>

        <Section id="cross-border-transfers" number={10} title="Cross-Border Data Transfers" delay={0.02}>
          <p>
            We primarily store and process personal data on servers located in India. Where a service provider
            (for example, a cloud hosting or analytics provider) processes data outside India, we take
            reasonable steps to ensure the transfer complies with the DPDP Act, including any restrictions on
            transfers to countries notified by the Government of India.
          </p>
        </Section>

        <Section id="data-security" number={11} title="Data Security" delay={0.02}>
          <p>
            We implement reasonable security practices and procedures, including technical, administrative,
            and physical safeguards, appropriate to the sensitivity of the information, to protect personal
            data against unauthorized access, alteration, disclosure, or destruction, in line with the
            Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or
            Information) Rules, 2011 and the DPDP Act. No method of transmission or storage is completely
            secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section id="breach-notification" number={12} title="Data Breach Notification" delay={0.02}>
          <p>
            In the event of a personal data breach, we will notify the Data Protection Board of India and
            affected Data Principals without undue delay, in accordance with the DPDP Rules, 2025, and take
            reasonable steps to mitigate any harm.
          </p>
        </Section>

        <Section id="childrens-privacy" number={13} title="Children's Privacy" delay={0.02}>
          <p>
            The Service is not directed at, and is not intended for use by, individuals under the age of 18. We
            do not knowingly collect personal data of children without verifiable parental or guardian consent,
            and we do not use children&apos;s personal data for behavioral monitoring or targeted advertising.
            If we become aware that we have collected personal data from a child without appropriate consent,
            we will take steps to delete it. If you believe a child has provided us with personal data, please
            contact us using the details in Section 15.
          </p>
        </Section>

        <Section id="your-rights" number={14} title="Your Rights as a Data Principal" delay={0.02}>
          <p>Subject to applicable law, you have the right to:</p>
          <Bullets
            items={[
              "Access a summary of the personal data we hold about you and the processing activities undertaken",
              "Correct and update inaccurate or incomplete personal data",
              "Erasure — request deletion of your personal data that is no longer necessary for the purpose it was collected, subject to our legal retention obligations",
              "Withdraw consent at any time (see Section 5)",
              "Grievance redressal — raise a complaint about how we handle your personal data (see Section 15)",
              "Nominate another individual to exercise these rights on your behalf in the event of your death or incapacity",
            ]}
          />
          <p>
            To exercise these rights, please contact us using the details in Section 15. We will respond within
            the timelines prescribed under applicable law.
          </p>
        </Section>

        <Section id="grievance-officer" number={15} title="Grievance Officer and Grievance Redressal" delay={0.02}>
          <p>
            In accordance with the Information Technology Act, 2000, the DPDP Act, and the Consumer Protection
            (E-Commerce) Rules, 2020, we have appointed a Grievance Officer to address your queries and
            complaints regarding this Policy and the processing of your personal data.
          </p>
          <div className="mt-4 rounded-2xl bg-foam-100 p-5 text-sm dark:bg-white/5">
            <p>
              <span className="font-semibold text-ocean-950 dark:text-foam-50">Grievance Officer:</span> {GRIEVANCE_OFFICER_NAME}
            </p>
            <p className="mt-1.5">
              <span className="font-semibold text-ocean-950 dark:text-foam-50">Email:</span>{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-teal-600 hover:text-teal-700 dark:text-teal-300">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="mt-1.5">
              <span className="font-semibold text-ocean-950 dark:text-foam-50">Phone:</span>{" "}
              <a href={`tel:${CONTACT_PHONE_HREF}`} className="text-teal-600 hover:text-teal-700 dark:text-teal-300">
                {CONTACT_PHONE_DISPLAY}
              </a>
            </p>
            <p className="mt-1.5">
              <span className="font-semibold text-ocean-950 dark:text-foam-50">Address:</span> {REGISTERED_ADDRESS}
            </p>
          </div>
          <p className="mt-4">
            We will acknowledge your complaint within 48 hours and endeavor to redress it within 30 days of
            receipt. If you are not satisfied with our response, you may escalate your complaint to the Data
            Protection Board of India, once operational, or to the appropriate consumer forum under the
            Consumer Protection Act, 2019.
          </p>
        </Section>

        <Section id="marketing" number={16} title="Marketing Communications" delay={0.02}>
          <p>
            With your consent, we may send you promotional offers, order recommendations, and service updates
            by SMS, email, push notification, or WhatsApp. You may opt out at any time using the unsubscribe
            link in our communications, through your app notification settings, or by contacting us at the
            details in Section 15. We will continue to send you essential, transactional communications (such
            as order confirmations) even if you opt out of marketing messages.
          </p>
        </Section>

        <Section id="third-party-links" number={17} title="Third-Party Links" delay={0.02}>
          <p>
            The Service may contain links to third-party websites or services that are not operated by us. We
            are not responsible for the privacy practices of those third parties, and we encourage you to
            review their privacy policies before providing them with any information.
          </p>
        </Section>

        <Section id="changes" number={18} title="Changes to This Privacy Policy" delay={0.02}>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or
            applicable law. We will notify you of material changes through the app, our website, or other
            appropriate means, and will update the &quot;Last Updated&quot; date above. Your continued use of
            the Service after changes take effect constitutes acceptance of the revised Policy.
          </p>
        </Section>

        <Section id="governing-law" number={19} title="Governing Law and Jurisdiction" delay={0.02}>
          <p>
            This Privacy Policy is governed by the laws of India. Any disputes arising out of or in connection
            with this Policy shall be subject to the exclusive jurisdiction of the courts at {JURISDICTION_CITY}.
          </p>
        </Section>

        <Section id="contact-us" number={20} title="Contact Us" delay={0.02}>
          <p>If you have questions, requests, or complaints regarding this Privacy Policy or our data practices, please contact us at:</p>
          <div className="mt-4 rounded-2xl bg-foam-100 p-5 text-sm dark:bg-white/5">
            <p className="font-display font-semibold text-ocean-950 dark:text-foam-50">{COMPANY_NAME}</p>
            <p className="mt-1.5">{REGISTERED_ADDRESS}</p>
            <p className="mt-1.5">
              Email:{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-teal-600 hover:text-teal-700 dark:text-teal-300">
                {CONTACT_EMAIL}
              </a>
            </p>
            <p className="mt-1.5">
              Phone:{" "}
              <a href={`tel:${CONTACT_PHONE_HREF}`} className="text-teal-600 hover:text-teal-700 dark:text-teal-300">
                {CONTACT_PHONE_DISPLAY}
              </a>
            </p>
          </div>
        </Section>
      </section>
    </PageTransition>
  );
}

/** /privacy: the version staff published (admin → Policy & legal), else the built-in Privacy Policy above. */
export default function PrivacyPolicy() {
  const published = usePublishedPolicy("privacy");
  if (published.status === "loading") return <PolicyLoading />;
  return published.status === "ready" ? <PolicyPage doc={published.doc} /> : <BuiltInPrivacy />;
}
